import React, { useContext, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import InputModern from 'components/ui/Input/InputModern';
import { AIContext } from 'contexts/AIContext';
import { formatUrl, getReadableByURL } from 'utils/getReadableByURL';
import { CompanyContext } from 'contexts/CompanyContext';
import { isValidUrl } from 'utils/validate';
import { proxyUrl } from 'utils/proxy';
import { CompanyOnboardingContext } from 'contexts/CompanyOnboardingContext';
import DynamicLoading, { websiteScrapLoadingMessages } from 'components/shared/DynamicLoading';
import { extractDominantColors } from 'utils/colorUtils';
import { languageOptions } from 'views/company_settings/components/BrandVoice';
import { useWhitelabel } from 'contexts/WhitelabelThemeContext';
import { TbWorldWww, TbSparkles } from 'react-icons/tb';
import useDarkMode from 'utils/hooks/useDarkMode';

export default function StepCompanyWebsite() {

  const { company } = useContext(CompanyContext);
  const { nextStep, setCurrentStepData, setStepCustomHandlers } = useContext(CompanyOnboardingContext);
  const { getByQueryChat, aiModels } = useContext(AIContext);
  const [ website, setWebsite ] = useState(company?.website);
  const [ isLoading, setIsLoading ] = useState(false);
  const { whitelabel } = useWhitelabel();
  const [ data, setData ] = useState({
    name: company?.name,
    description: company?.description,
    image: company?.image,
    language_iso_code: company?.language_iso_code,
    language: company?.language,
    audience_types: company?.audience_types,
    interests: company?.interests,
    language_types: company?.language_types,
    palettes: company?.palettes,
    tones: company?.tones,
    emotions: company?.emotions,
    syntaxes: company?.syntaxes,
    characters: company?.characters,
  });

  // Update context when data changes
  useEffect(() => {
    if (website) {
      setCurrentStepData({ website: formatUrl(website), ...data });
    }
  }, [website, data, setCurrentStepData]);

  const onNextStep = async () => {
    try {
      setIsLoading(true);
      const data = await generate();
      await nextStep(data);
    } catch (err) {
      if (isValidUrl(formatUrl(website))) {
        nextStep({ website: formatUrl(website) });
      } else {
        nextStep();
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Register custom handlers for this step
  useEffect(() => {
    setStepCustomHandlers({
      onNextStep,
      loading: isLoading
    });
    return () => {
      setStepCustomHandlers({});
    };
  }, [isLoading, website, data, setStepCustomHandlers]);

  const getBase64FromUrl = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob); 
        reader.onloadend = () => {
          const base64data = reader.result;   
          resolve(base64data);
        }
        reader.onerror = () => {
          reject(new Error('Failed to read blob as base64'));
        }
      });
    } catch (err) {
      console.error(`Failed to fetch image from ${url}:`, err);
      throw err; // Re-throw to be caught by caller
    }
  }

  const generateNameAndDescription = async (websiteContent) => {    
    if (websiteContent?.length > 200) {
      const response = await getByQueryChat(
        `
          Website content from ${website}: 
          ${websiteContent}
        `,  
        false, 
        true, 
        `
          When you are given a website content you should answer a valid JSON with the following information:

          Generate a detailed, objective, and professional description for the company.
            - The description must start with: "{company name} is a".
            - The description should be detailed, factual, and clear, and as long as possible with a maximum length of 3,000 characters.
            - Separate into multiple paragraphs for enhanced readability.

          Additionally, extract the following details:
            - Company Name: Extract the company name from the content.
            - Website Logo URL: Provide the logo's absolute URL if found, otherwise return null or empty. Use the base URL (${website}) for reference.
            - Audience Types: List the intended audiences the content addresses.
            - Interests: List the interests or topics the content addresses.
            - Tones: Identify the tone(s) used in the content.
            - Emotions: Identify any emotional appeals present in the content.
            - Syntaxes: Return an array describing how the content is structured, such as writing style, clarity, actionability, and organization. Minimum of 3 words per syntax.
            - Language Types: Specify the type of language used, maximum of 2 words per language type.
            - Characters: A brand's character is the set of human characteristics that define the brand.
            - Language ISO: The language code (e.g., en, es).
            - Language Name: The full name of the language (e.g., English, Spanish).
        `, 
        undefined, 
        undefined, 
        undefined, 
        aiModels.find(e => !!e?.sysDefault)?.model, 
        {
          "name": "website_content",
          "schema": {
            "type": "object",
            "properties": {
              "company_name": {
                "type": "string",
                "description": "The name of the company."
              },
              "company_description": {
                "type": "string",
                "description": "A description of the company, as long as possible up to 3000 characters. Separate into multiple paragraphs for enhanced readability."
              },
              "audience_types": {
                "type": "array",
                "description": "List of target audiences.",
                "items": {
                  "type": "string"
                }
              },
              "interests": {
                "type": "array",
                "description": "List of interests or topics the content addresses.",
                "items": {
                  "type": "string"
                }
              },
              "tones": {
                "type": "array",
                "description": "Tone used in the content, can be one or more ",
                "items": {
                  "type": "string",
                }
              },
              "emotions": {
                "type": "array",
                "description": "Emotional tone of the content, can be one or more ",
                "items": {
                  "type": "string"
                }
              },
              "syntaxes": {
                "type": "array",
                "description": "Syntax styles that should be followed, can be one or more ",
                "items": {
                  "type": "string"
                }
              },
              "language_types": {
                "type": "array",
                "description": "The type of language to use ",
                "items": {
                  "type": "string"
                }
              },
              "characters": {
                "type": "array",
                "description": "A brand's character is the set of human characteristics that define the brand.",
                "items": {
                  "type": "string"
                }
              },
              "language_iso_code": {
                "type": "string",
                "description": "ISO language code (e.g., en, es)."
              },
              "language": {
                "type": "string",
                "description": "Human-readable name of the language (e.g., English, Spanish)."
              }
            },
            "required": [
              "company_name",
              "company_description",
              "audience_types",
              "interests",
              "tones",
              "emotions",
              "syntaxes",
              "language_types",
              "characters",
              "language_iso_code",
              "language"
            ],
            "additionalProperties": false
          },
          "strict": true
        }
      );
      
      if (response) {
        // getByQueryChat parses the envelope itself whenever a json schema is passed,
        // and one is passed above — only the legacy no-schema path returns a string.
        // Parsing unconditionally threw "[object Object]" is not valid JSON, which the
        // caller swallowed, leaving onboarding with nothing to fill in.
        const parsed = typeof response === 'string' ? JSON.parse(response) : response;

        let {
          company_name,
          company_description,
          audience_types,
          interests,
          tones,
          emotions,
          syntaxes,
          language_types,
          characters,
          language_iso_code,
          language
        } = parsed;

        let finalImage = undefined;

        // Wrap all image fetching logic in a single try-catch to ensure we continue
        try {
          // // Try getting image from original URL first
          // if (image && !image?.toLowerCase()?.includes('.svg')) {
          //   try {
          //     finalImage = await getBase64FromUrl(proxyUrl(image));
          //   } catch (err) {
          //     console.error('Failed to fetch original image:', err);
          //   }
          // }

          // If still no image, try favicon
          if (!finalImage) {
            try {
              finalImage = await getBase64FromUrl(proxyUrl(`https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${website}&size=256`));
            } catch (err) {
              console.error('Failed to fetch favicon:', err);
            }
          }
          
          // If first attempt failed, try Clearbit
          if (!finalImage) {
            try {
              finalImage = await getBase64FromUrl(`https://logo.clearbit.com/${website}?size=256`);
            } catch (err) {
              console.error('Failed to fetch Clearbit image:', err);
            }
          }
        } catch (err) {
          console.error('Error in image fetching process:', err);
          // Continue without image
        }

        let palettes = data?.palettes || [];
        if (!palettes?.length && finalImage) {
          try {
            palettes = await extractDominantColors(finalImage);
          } catch (err) {
            console.error('Failed to extract colors:', err);
          }
        }

        const defaultLanguage = languageOptions?.[0];
        let _language_iso_code = language_iso_code?.toString()?.replace(/[^a-zA-Z]/g, '') || defaultLanguage?.iso_code;
        if (!languageOptions?.find(e => e?.iso_code == _language_iso_code)) {
          _language_iso_code = defaultLanguage?.iso_code;
        }
        let _language = languageOptions.find(e => e?.iso_code == _language_iso_code)?.value;

        const newData = {
          ...data,
          name: company_name,
          description: company_description,
          image: finalImage ? { url: finalImage } : undefined,
          audience_types: audience_types || data?.audience_types,
          interests: interests || data?.interests,
          tones: tones || data?.tones,
          emotions: emotions || data?.emotions,
          syntaxes: syntaxes || data?.syntaxes,
          language_types: language_types || data?.language_types,
          characters: characters || data?.characters,
          language_iso_code: _language_iso_code,
          language: _language,
          palettes: palettes || data?.palettes,
          website: website
        }
        setData(newData);

        return newData;
      }
    }

    setIsLoading(true);
  }

  const generate = async () => {
    if (!website) return undefined;
    try {
      const { textContent } = await getReadableByURL(formatUrl(website));
      const data = await generateNameAndDescription(textContent);
      return data;
    } catch (err) {
      console.error(err);
    }
  };

  const [isDarkMode] = useDarkMode();
  const [loadingStage, setLoadingStage] = useState(0);

  // Loading stages animation
  React.useEffect(() => {
    if (!isLoading) {
      setLoadingStage(0);
      return;
    }

    const stages = [
      { duration: 2000 },  // Analyzing website
      { duration: 3000 },  // Extracting brand info
      { duration: 2000 },  // Detecting colors
      { duration: 3000 },  // Generating description
    ];

    let currentStage = 0;
    const timers = [];

    const advanceStage = () => {
      currentStage++;
      if (currentStage < stages.length) {
        setLoadingStage(currentStage);
        timers.push(setTimeout(advanceStage, stages[currentStage].duration));
      }
    };

    setLoadingStage(0);
    timers.push(setTimeout(advanceStage, stages[0].duration));

    return () => timers.forEach(timer => clearTimeout(timer));
  }, [isLoading]);

  return (
    <>
      {isLoading ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md space-y-8"
          >
            {/* Animated Logo/Icon */}
            <motion.div className="flex justify-center">
              <motion.div
                animate={{ 
                  rotate: 360,
                  scale: [1, 1.1, 1]
                }}
                transition={{ 
                  rotate: { duration: 2, repeat: Infinity, ease: "linear" },
                  scale: { duration: 1, repeat: Infinity }
                }}
                className="w-24 h-24 rounded-3xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-2xl shadow-indigo-500/50"
              >
                <TbSparkles className="text-5xl text-white" />
              </motion.div>
            </motion.div>

            {/* Main Title */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <h3 className={`text-2xl font-black mb-2 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Analyzing Your Website...
              </h3>
              <p className={`text-sm ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Our AI is extracting your brand information
              </p>
            </motion.div>

            {/* Loading Stages */}
            <div className="space-y-4">
              {[
                { icon: '🌐', label: 'Analyzing website', color: 'from-blue-500 to-cyan-500' },
                { icon: '🎨', label: 'Extracting brand colors', color: 'from-purple-500 to-pink-500' },
                { icon: '🖼️', label: 'Detecting logo', color: 'from-orange-500 to-red-500' },
                { icon: '📝', label: 'Generating description', color: 'from-green-500 to-emerald-500' },
              ].map((stage, index) => {
                const isActive = loadingStage === index;
                const isComplete = loadingStage > index;
                
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`flex items-center gap-4 p-4 rounded-2xl transition-all ${
                      isActive
                        ? isDarkMode
                          ? 'bg-white/10 border-2 border-purple-500'
                          : 'bg-white border-2 border-purple-500 shadow-lg'
                        : isComplete
                          ? isDarkMode
                            ? 'bg-white/5 border-2 border-green-500/50'
                            : 'bg-green-50 border-2 border-green-500/50'
                          : isDarkMode
                            ? 'bg-white/[0.02] border-2 border-white/5'
                            : 'bg-gray-50 border-2 border-gray-200'
                    }`}
                  >
                    {/* Icon/Status */}
                    <div className={`relative w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                      isComplete
                        ? 'bg-gradient-to-br from-green-500 to-emerald-500'
                        : isActive
                          ? `bg-gradient-to-br ${stage.color}`
                          : isDarkMode
                            ? 'bg-gray-700'
                            : 'bg-gray-200'
                    }`}>
                      {isComplete ? (
                        <motion.div
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ type: "spring", stiffness: 200 }}
                        >
                          <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </motion.div>
                      ) : (
                        <span className={`text-2xl ${isActive ? '' : 'opacity-50'}`}>
                          {stage.icon}
                        </span>
                      )}
                      
                      {/* Animated Ring for Active Stage */}
                      {isActive && (
                        <motion.div
                          className="absolute inset-0 rounded-xl border-2 border-purple-500"
                          animate={{ scale: [1, 1.1, 1], opacity: [1, 0.5, 1] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        />
                      )}
                    </div>

                    {/* Label */}
                    <div className="flex-1">
                      <div className={`font-semibold ${
                        isActive || isComplete
                          ? isDarkMode ? 'text-white' : 'text-gray-900'
                          : isDarkMode ? 'text-gray-500' : 'text-gray-400'
                      }`}>
                        {stage.label}
                      </div>
                    </div>

                    {/* Loading Spinner for Active */}
                    {isActive && (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-5 h-5"
                      >
                        <svg className="w-5 h-5 text-purple-500" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </div>

            {/* Progress Bar */}
            <div className="space-y-2">
              <div className={`h-2 rounded-full overflow-hidden ${
                isDarkMode ? 'bg-white/10' : 'bg-gray-200'
              }`}>
                <motion.div
                  className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 bg-[length:200%_100%]"
                  initial={{ width: '0%' }}
                  animate={{ 
                    width: `${((loadingStage + 1) / 4) * 100}%`,
                    backgroundPosition: ['0% 0%', '100% 0%']
                  }}
                  transition={{ 
                    width: { duration: 0.5 },
                    backgroundPosition: { duration: 2, repeat: Infinity, ease: "linear" }
                  }}
                />
              </div>
              <div className={`text-xs text-center font-mono ${
                isDarkMode ? 'text-gray-500' : 'text-gray-400'
              }`}>
                {Math.round(((loadingStage + 1) / 4) * 100)}% Complete
              </div>
            </div>

            {/* Fun Fact */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className={`p-4 rounded-2xl text-center ${
                isDarkMode 
                  ? 'bg-indigo-500/10 border border-indigo-500/20' 
                  : 'bg-indigo-50 border border-indigo-200'
              }`}
            >
              <p className={`text-sm ${
                isDarkMode ? 'text-indigo-300' : 'text-indigo-700'
              }`}>
                ✨ <strong>Did you know?</strong> Our AI can analyze thousands of websites per second
              </p>
            </motion.div>
          </motion.div>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-4 md:gap-6 flex-1">
            {/* Animated Icon */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
              className="flex justify-center mb-2 md:mb-4"
            >
              <div className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 rounded-2xl md:rounded-3xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-2xl shadow-indigo-500/50">
                <TbWorldWww className="text-3xl md:text-4xl lg:text-5xl text-white" />
              </div>
            </motion.div>

            {/* Title */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <h3 className={`!text-2xl md:!text-3xl lg:!text-4xl font-black mb-2 md:mb-3 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Let's find your website
            </h3>
              <p className={`text-base md:text-lg ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Our AI will automatically extract your brand details
              </p>
            </motion.div>

            {/* Input */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-2 md:space-y-3"
            >
              <label className={`block text-xs md:text-sm font-bold ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Company Website
              </label>
            <InputModern 
              name="website"
              placeholder="https://your-company.com"
                className={`!w-full !px-4 !py-3 md:!px-6 md:!py-4 text-base md:text-lg !rounded-xl md:!rounded-2xl !border-2 focus:!border-purple-500 transition-all ${
                  isDarkMode
                    ? '!bg-white/5 !border-white/10 !text-white placeholder:!text-gray-500'
                    : '!bg-gray-50 !border-gray-200 !text-gray-900 placeholder:!text-gray-400'
                }`}
                autoFocus={true}
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onNextStep();
                }
              }}
            />
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className={`text-xs md:text-sm ${
                  isDarkMode ? 'text-gray-500' : 'text-gray-500'
                }`}
              >
                Enter your website URL and we'll extract brand colors, logo, and description
              </motion.p>
            </motion.div>

            {/* Feature Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="grid grid-cols-3 gap-2 md:gap-3"
            >
              {[
                { icon: '🎨', title: 'Brand Colors', desc: 'Extract palette' },
                { icon: '📝', title: 'Description', desc: 'Auto-generate' },
                { icon: '🖼️', title: 'Logo', desc: 'Detect & save' }
              ].map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className={`p-3 md:p-4 rounded-lg md:rounded-xl text-center ${
                    isDarkMode 
                      ? 'bg-white/5 border border-white/10' 
                      : 'bg-gray-50 border border-gray-200'
                  }`}
                >
                  <div className="text-2xl md:text-3xl mb-1 md:mb-2">{feature.icon}</div>
                  <div className={`text-xs md:text-sm font-bold mb-0.5 md:mb-1 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {feature.title}
                  </div>
                  <div className={`text-[10px] md:text-xs ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-500'
                  }`}>
                    {feature.desc}
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* AI Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8 }}
              className={`flex items-center justify-center gap-1.5 md:gap-2 p-3 md:p-4 rounded-xl md:rounded-2xl ${
                isDarkMode 
                  ? 'bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20' 
                  : 'bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200'
              }`}
            >
              <TbSparkles className={`text-lg md:text-xl ${
                isDarkMode ? 'text-purple-400' : 'text-purple-600'
              }`} />
              <p className={`text-xs md:text-sm font-semibold ${
                isDarkMode ? 'text-purple-300' : 'text-purple-700'
              }`}>
                Powered by AI • Takes ~10 seconds
              </p>
            </motion.div>
          </div>
        </>
      )}
    </>
  );
}
